import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { organizationsAPI } from '../../../services/api';

const PERMISSION_LABELS = {
  canCreateBots: { key: 'roles.permissions.canCreateBots', categoryKey: 'roles.categories.bots' },
  canEditBots: { key: 'roles.permissions.canEditBots', categoryKey: 'roles.categories.bots' },
  canDeleteBots: { key: 'roles.permissions.canDeleteBots', categoryKey: 'roles.categories.bots' },
  canViewBots: { key: 'roles.permissions.canViewBots', categoryKey: 'roles.categories.bots' },
  canManageNumbers: { key: 'roles.permissions.canManageNumbers', categoryKey: 'roles.categories.numbers' },
  canManageIntegrations: { key: 'roles.permissions.canManageIntegrations', categoryKey: 'roles.categories.integrations' },
  canInviteMembers: { key: 'roles.permissions.canInviteMembers', categoryKey: 'roles.categories.members' },
  canManageMembers: { key: 'roles.permissions.canManageMembers', categoryKey: 'roles.categories.members' },
  canRemoveMembers: { key: 'roles.permissions.canRemoveMembers', categoryKey: 'roles.categories.members' },
  canManageBilling: { key: 'roles.permissions.canManageBilling', categoryKey: 'roles.categories.billing' },
  canViewAnalytics: { key: 'roles.permissions.canViewAnalytics', categoryKey: 'roles.categories.analytics' },
  canViewChats: { key: 'roles.permissions.canViewChats', categoryKey: 'roles.categories.conversations' },
  canManageChats: { key: 'roles.permissions.canManageChats', categoryKey: 'roles.categories.conversations' },
  canManageOrgSettings: { key: 'roles.permissions.canManageOrgSettings', categoryKey: 'roles.categories.settings' }
};

const ROLE_INFO = {
  owner: { nameKey: 'roles.roleNames.owner', descKey: 'roles.roleDescriptions.owner' },
  admin: { nameKey: 'roles.roleNames.admin', descKey: 'roles.roleDescriptions.admin' },
  member: { nameKey: 'roles.roleNames.member', descKey: 'roles.roleDescriptions.member' },
  viewer: { nameKey: 'roles.roleNames.viewer', descKey: 'roles.roleDescriptions.viewer' }
};

export default function RolesTab({ organization, currentUserRole, orgId }) {
  const { t } = useTranslation('organization');
  const [roles, setRoles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(null);
  const [editedPermissions, setEditedPermissions] = useState({});
  const [saving, setSaving] = useState(false);

  const canEdit = currentUserRole === 'owner';

  useEffect(() => {
    if (orgId) {
      loadRoles();
    }
  }, [orgId]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await organizationsAPI.getRoles(orgId);
      if (response.ok) {
        setRoles(response.roles);
      } else {
        console.error('Error loading roles:', response);
        alert(t('roles.errorLoadingRoles'));
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      alert(t('roles.errorLoadingRoles') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditRole = (roleName) => {
    setEditingRole(roleName);
    setEditedPermissions(roles[roleName].permissions);
  };

  const handleTogglePermission = (permKey) => {
    setEditedPermissions(prev => ({
      ...prev,
      [permKey]: !prev[permKey]
    }));
  };

  const handleSave = async () => {
    if (!editingRole) return;

    try {
      setSaving(true);

      const response = await organizationsAPI.updateRole(orgId, editingRole, {
        permissions: editedPermissions
      });

      if (response.ok) {
        alert(t('roles.saveSuccess', { role: editingRole, count: response.affectedMembers }));
        await loadRoles();
        setEditingRole(null);
      } else {
        alert(t('roles.errorPrefix') + (response.error || t('roles.errorCouldNotUpdate')));
      }
    } catch (error) {
      console.error('Error saving role:', error);
      alert(t('roles.errorSaving') + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingRole(null);
    setEditedPermissions({});
  };

  if (loading) {
    return (
      <div className="roles-tab">
        <div className="loader">{t('roles.loading')}</div>
      </div>
    );
  }

  if (!roles) {
    return (
      <div className="roles-tab">
        <p>{t('roles.couldNotLoad')}</p>
      </div>
    );
  }

  // Group permissions by category
  const permissionsByCategory = {};
  Object.entries(PERMISSION_LABELS).forEach(([key, { key: labelKey, categoryKey }]) => {
    const category = t(categoryKey);
    if (!permissionsByCategory[category]) {
      permissionsByCategory[category] = [];
    }
    permissionsByCategory[category].push({ key, label: t(labelKey) });
  });

  return (
    <div className="roles-tab">
      <div className="tab-header">
        <div>
          <h2>{t('roles.title')}</h2>
          <p className="tab-description">
            {canEdit
              ? t('roles.descriptionOwner')
              : t('roles.descriptionViewer')}
          </p>
        </div>
      </div>

      <div className="roles-grid">
        {Object.entries(roles).map(([roleKey, roleData]) => {
          const isEditing = editingRole === roleKey;
          const permissions = isEditing ? editedPermissions : roleData.permissions;
          const isOwnerRole = roleKey === 'owner';
          const roleInfo = ROLE_INFO[roleKey];

          return (
            <div key={roleKey} className={`role-card ${isEditing ? 'editing' : ''}`}>
              <div className="role-header">
                <h3>
                  <span className={`role-badge ${roleKey}`}>{roleKey}</span>
                  {roleInfo ? t(roleInfo.nameKey) : roleData.name}
                </h3>
                <p className="role-description">{roleInfo ? t(roleInfo.descKey) : roleData.description}</p>
              </div>

              {!isEditing ? (
                <>
                  <div className="permissions-summary">
                    {Object.entries(permissionsByCategory).map(([category, perms]) => (
                      <div key={category} className="permission-category">
                        <h4>{category}</h4>
                        <ul>
                          {perms.map(({ key, label }) => (
                            <li key={key} className={permissions[key] ? 'granted' : 'denied'}>
                              <span className="permission-icon">
                                {permissions[key] ? '✓' : '✗'}
                              </span>
                              {label}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  {canEdit && (
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => handleEditRole(roleKey)}
                      style={{ marginTop: '16px', width: '100%' }}
                    >
                      {t('roles.editPermissions')}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {isOwnerRole && (
                    <div className="warning-box">
                      {t('roles.ownerWarning')}
                    </div>
                  )}

                  <div className="permissions-editor">
                    {Object.entries(permissionsByCategory).map(([category, perms]) => (
                      <div key={category} className="permission-category">
                        <h4>{category}</h4>
                        {perms.map(({ key, label }) => {
                          const criticalOwnerPerms = ['canManageOrgSettings', 'canManageMembers', 'canManageBilling'];
                          const isLocked = isOwnerRole && criticalOwnerPerms.includes(key);

                          return (
                            <label key={key} className="permission-checkbox">
                              <input
                                type="checkbox"
                                checked={permissions[key]}
                                onChange={() => !isLocked && handleTogglePermission(key)}
                                disabled={saving || isLocked}
                              />
                              <span>{label}</span>
                              {isLocked && <span className="locked-badge">{t('roles.locked')}</span>}
                            </label>
                          );
                        })}
                      </div>
                    ))}
                  </div>

                  <div className="role-actions">
                    <button
                      className="btn-primary btn-sm"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? t('roles.saving') : t('roles.save')}
                    </button>
                    <button
                      className="btn-secondary btn-sm"
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      {t('roles.cancel')}
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {canEdit && (
        <div className="info-box">
          <strong>{t('roles.importantLabel')}</strong> {t('roles.footerNote')}
        </div>
      )}
    </div>
  );
}
